/**
 * Checklist da Análise (perguntas Sim/Não migradas da antiga tela de
 * Relatório de Análise) e importação das respostas legadas.
 *
 * Cada pergunta tem sua própria chave na estrutura chave-valor da proposta
 * (analise.dados -> tb_proposta_dado). Os agrupamentos temáticos são os
 * mesmos da tela legada (relatorioAnalise.vue).
 *
 * A `chaveLegado` referencia o campo correspondente no JSON antigo gravado
 * em tb_proposta.relatorio_analise — usada apenas para importar a resposta
 * quando o campo novo ainda está vazio.
 */

export const OPCOES_SIM_NAO = ['Sim', 'Não', 'N/A']

export const GRUPOS_CHECKLIST_ANALISE = [
  {
    titulo: 'Objetivos e Metas',
    itens: [
      { chave: 'ds_indicadores_objetivo_geral', pergunta: 'O objetivo geral define o que se pretende com o projeto?' },
      { chave: 'ds_indicadores_objetivo_especifico', pergunta: 'Os objetivos específicos e metas propiciam o atingimento do objetivo geral?' },
      { chave: 'ds_indicadores_objetivo_compativel', pergunta: 'Os objetivos são compatíveis com as diretrizes estatutárias da FBB?' },
      { chave: 'ds_indicadores_objetivo_estruturado', pergunta: 'Compatíveis com as diretrizes de programa estruturado?' },
      { chave: 'ds_indicadores_objetivo_estrategico', pergunta: 'Compatíveis com o plano de trabalho de parcerias estratégicas?' },
      { chave: 'existe_critica_objetivos_metas_indicadores', pergunta: 'Existe crítica aos objetivos, metas e indicadores?' }
    ]
  },
  {
    titulo: 'Divulgação do Projeto',
    itens: [
      { chave: 'ds_acoes_comunicacao_memoria_calculo', pergunta: 'Recursos do Plano de Comunicação previstos na memória de cálculo?' },
      { chave: 'ds_divulgacao_banner', pergunta: 'Previsto o uso de banner obrigatório de apoio da Fundação?' },
      { chave: 'ds_divulgacao_identificacao', pergunta: 'Prevista a identificação de veículos adquiridos com recursos do projeto?' },
      { chave: 'ds_divulgacao_placa', pergunta: 'Prevista placa provisória e definitiva em obras civis?' },
      { chave: 'ds_divulgacao_solenizacao', pergunta: 'Prevista atividade de solenização de assinatura de convênio?' }
    ]
  },
  {
    titulo: 'Valores e Contrapartida',
    itens: [
      { chave: 'ds_valores_itens', pergunta: 'Itens de despesa compatíveis com objetivos, metas e atividades?' },
      { chave: 'ds_valores_contrapartida', pergunta: 'A contrapartida atende aos critérios dos normativos vigentes?' },
      { chave: 'ds_valores_deacordo', pergunta: 'Valores de acordo com os praticados no mercado?' },
      { chave: 'ds_valores_enquadrados', pergunta: 'Itens sem orçamento enquadrados nas diretrizes de dispensa?' },
      { chave: 'ds_valores_vedacoes', pergunta: 'Observadas as vedações de itens de despesa?' },
      { chave: 'ds_valores_industrial', pergunta: 'Observados itens para obra civil / bens fixos industriais?' },
      { chave: 'ds_valores_formacao', pergunta: 'Observados itens para formação, capacitação e/ou consultorias?' },
      { chave: 'conforme_obra_civil', pergunta: 'Verificada conformidade específica para obra civil?' },
      { chave: 'ds_valores_ajustes', pergunta: 'Foi realizado ajuste nos valores apresentados?' }
    ]
  },
  {
    titulo: 'Liberação de Recursos',
    itens: [
      { chave: 'ds_liberacao_adiantamentos', pergunta: 'Parâmetros de adiantamento atendidos conforme normativos vigentes?' }
    ]
  },
  {
    titulo: 'Prazo de Execução',
    observacaoChave: 'ds_execucao_observacoes',
    itens: [
      { chave: 'ds_execucao_compatibilidade', pergunta: 'Há compatibilidade entre o prazo de execução e as atividades?' }
    ]
  },
  {
    titulo: 'Regularidade da Proposta e da Proponente',
    observacaoChave: 'ds_regularidade_observacoes',
    itens: [
      { chave: 'ds_regularidade_objetivos', pergunta: 'Proposta compatível com os objetivos estatutários da entidade?' },
      { chave: 'ds_regularidade_documentacao', chaveLegado: 'ds_regularidade_documentação', pergunta: 'Apresentou toda a documentação exigida para análise?' },
      { chave: 'ds_regularidade_cadastral', pergunta: 'Encontra-se em regularidade cadastral?' },
      { chave: 'ds_regularidade_fiscal', pergunta: 'Em conformidade fiscal e documental de parceria estratégica?' }
    ]
  },
  {
    titulo: 'Enquadramento e Parecer Jurídico',
    observacaoChave: 'ds_enquadramento_observacoes',
    itens: [
      { chave: 'ds_enquadramento_orcamento', pergunta: 'Proposta enquadrada no controle orçamentário da FBB?' },
      { chave: 'ds_enquadramento_minuta', pergunta: 'Foi utilizada a minuta padrão aprovada?' }
    ]
  }
]

const vazio = (valor) => valor === null || valor === undefined || valor === ''

/**
 * Monta o mapa { chaveNova: valorLegado } das respostas do JSON antigo
 * (tb_proposta.relatorio_analise) que devem ser importadas para a nova
 * estrutura chave-valor (analise.dados). Importa somente quando o campo
 * novo correspondente ainda está vazio — nunca sobrescreve resposta nova.
 *
 * O chamador aplica o resultado com this.$set para manter a reatividade.
 */
export function respostasLegadasParaImportar (proposta) {
  let legado = {}
  if (proposta && proposta.relatorio_analise) {
    try {
      legado = typeof proposta.relatorio_analise === 'string'
        ? JSON.parse(proposta.relatorio_analise)
        : proposta.relatorio_analise
    } catch (e) {
      legado = {}
    }
  }
  if (!legado || typeof legado !== 'object') {
    return {}
  }

  const dados = (proposta && proposta.dados && !Array.isArray(proposta.dados)) ? proposta.dados : {}
  const importar = {}

  GRUPOS_CHECKLIST_ANALISE.forEach((grupo) => {
    const pares = grupo.itens.map((item) => [item.chave, item.chaveLegado || item.chave])
    if (grupo.observacaoChave) {
      pares.push([grupo.observacaoChave, grupo.observacaoChave])
    }
    pares.forEach(([chave, chaveLegado]) => {
      if (vazio(dados[chave]) && !vazio(legado[chaveLegado])) {
        importar[chave] = legado[chaveLegado]
      }
    })
  })

  return importar
}



// WEBPACK FOOTER //
// ./src/mixins/checklistAnalise.js